import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowRight, AlertCircle, RefreshCw, Home, Mail, Calendar, MapPin, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ResumeWelcome - Welcome screen for deep link session resume
 * 
 * Shows:
 * - Loading state while fetching session
 * - Welcome back message with draft summary
 * - Error states with human-friendly messages
 * - CTA to continue the conversation
 */
export const ResumeWelcome = ({ token, onSessionLoaded, onError, locale = 'it' }) => {
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [sessionData, setSessionData] = useState(null);
  const [errorInfo, setErrorInfo] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  const isIT = locale === 'it';

  useEffect(() => {
    const loadSession = async () => {
      try {
        const response = await axios.get(`${API}/resume/${token}?locale=${locale}`);
        const data = response.data;
        
        if (data.status === 'error') {
          setStatus('error');
          setErrorInfo({
            code: data.error_code,
            message: data.error_message
          });
          return;
        }
        
        setSessionData(data);
        setStatus('success');
        
      } catch (err) {
        console.error('Resume error:', err);
        setStatus('error');
        setErrorInfo({
          code: 'unknown_error',
          message: isIT 
            ? 'Si è verificato un errore. Riprova o contattaci per assistenza.'
            : 'An error occurred. Please try again or contact us for assistance.'
        });
      }
    };
    
    loadSession();
  }, [token, locale, isIT]);

  const handleContinue = () => {
    if (sessionData && onSessionLoaded) {
      onSessionLoaded(sessionData);
    }
  };

  const handleRetry = () => {
    setStatus('loading');
    setErrorInfo(null);
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  // ============== LOADING STATE ==============
  if (status === 'loading') {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-umbria-cream via-white to-umbria-sage/10">
        <div className="text-center animate-fade-in">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-umbria-olive/10 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-umbria-olive animate-spin" />
          </div>
          <h2 className="text-xl font-medium text-umbria-charcoal mb-2">
            {isIT ? 'Recupero la tua sessione...' : 'Retrieving your session...'}
          </h2>
          <p className="text-gray-500">
            {isIT ? 'Un momento' : 'Just a moment'}
          </p>
        </div>
      </div>
    );
  }

  // ============== ERROR STATE ==============
  if (status === 'error') {
    const errorIcons = {
      token_expired: Calendar,
      token_invalid: AlertCircle,
      token_revoked: RefreshCw,
      session_not_found: AlertCircle,
      unknown_error: AlertCircle
    };
    const ErrorIcon = errorIcons[errorInfo?.code] || AlertCircle;
    
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-umbria-cream via-white to-red-50/30 p-4">
        <div className="max-w-md w-full text-center animate-fade-in">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-50 flex items-center justify-center">
            <ErrorIcon className="w-10 h-10 text-red-400" />
          </div>
          
          <h2 className="text-2xl font-semibold text-umbria-charcoal mb-3">
            {isIT ? 'Link non valido' : 'Invalid Link'}
          </h2>
          
          <p className="text-gray-600 mb-8 leading-relaxed">
            {errorInfo?.message}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {errorInfo?.code === 'token_expired' && (
              <button
                onClick={handleRetry}
                className="px-6 py-3 bg-umbria-olive text-white rounded-xl font-medium hover:bg-umbria-olive/90 transition-colors flex items-center justify-center gap-2"
              >
                <RefreshCw size={18} />
                {isIT ? 'Richiedi nuovo link' : 'Request new link'}
              </button>
            )}
            
            <button
              onClick={handleGoHome}
              className="px-6 py-3 bg-white border border-gray-200 text-umbria-charcoal rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <Home size={18} />
              {isIT ? 'Nuova conversazione' : 'New conversation'}
            </button>
          </div>
          
          {/* Error code for debugging */}
          <p className="text-xs text-gray-400 mt-8">
            {isIT ? 'Codice errore' : 'Error code'}: {errorInfo?.code}
          </p>
        </div>
      </div>
    );
  }

  // ============== SUCCESS STATE ==============
  const summary = sessionData?.draft_summary || {};
  const thread = sessionData?.thread || {};
  const briefing = sessionData?.briefing || {};
  const updates = sessionData?.updates || [];
  
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-umbria-cream via-white to-umbria-sage/10 p-4 overflow-y-auto">
      <div className="max-w-lg w-full animate-fade-in py-8">
        {/* Welcome header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-umbria-olive to-umbria-sage flex items-center justify-center shadow-lg">
            <span className="text-4xl">👋</span>
          </div>
          
          <h1 className="text-3xl font-bold text-umbria-charcoal mb-2">
            {summary.relationship_name 
              ? (isIT ? `Bentornato, ${summary.relationship_name}!` : `Welcome back, ${summary.relationship_name}!`)
              : (isIT ? 'Bentornato!' : 'Welcome back!')
            }
          </h1>
          
          {summary.last_activity_ago && (
            <p className="text-gray-500">
              {isIT ? 'Ultima attività' : 'Last activity'}: {summary.last_activity_ago}
            </p>
          )}
        </div>
        
        {/* Draft summary card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-umbria-charcoal mb-4 flex items-center gap-2">
              <MapPin size={20} className="text-umbria-olive" />
              {isIT ? 'Riepilogo bozza' : 'Draft Summary'}
            </h2>
            
            <div className="space-y-3">
              {/* Dates */}
              {summary.dates && (
                <div className="flex items-start gap-3 text-sm">
                  <Calendar size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{summary.dates}</span>
                </div>
              )}
              
              {/* Party */}
              {summary.party && (
                <div className="flex items-start gap-3 text-sm">
                  <Users size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{summary.party}</span>
                </div>
              )}
              
              {/* Base area */}
              {summary.base_area && (
                <div className="flex items-start gap-3 text-sm">
                  <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    {isIT ? 'Base' : 'Base'}: {summary.base_area}
                  </span>
                </div>
              )}
              
              {/* Interests */}
              {summary.interests && summary.interests.length > 0 && (
                <div className="flex items-start gap-3 text-sm">
                  <span className="text-gray-400 mt-0.5 flex-shrink-0">✨</span>
                  <span className="text-gray-700">
                    {isIT ? 'Interessi' : 'Interests'}: {summary.interests.join(', ')}
                  </span>
                </div>
              )}
              
              {/* Draft progress */}
              {summary.has_draft && (
                <div className="mt-4 p-3 bg-umbria-olive/5 rounded-xl border border-umbria-olive/10">
                  <p className="text-sm font-medium text-umbria-charcoal">
                    {isIT 
                      ? `📍 ${summary.experiences_count} tappe nel tuo itinerario`
                      : `📍 ${summary.experiences_count} stops in your itinerary`
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
          
          {/* Updates section */}
          {updates.length > 0 && (
            <div className="border-t border-gray-100 p-6 bg-gradient-to-b from-amber-50/50">
              <h3 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                <span>🔔</span>
                {isIT ? 'Novità da quando sei andato via' : 'Updates since you left'}
              </h3>
              <ul className="space-y-2">
                {updates.map((update, idx) => (
                  <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-amber-500 mt-1">•</span>
                    {update}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {/* Continue button */}
        <button
          onClick={handleContinue}
          className="w-full py-4 px-6 bg-gradient-to-r from-umbria-olive to-umbria-sage text-white rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] flex items-center justify-center gap-3"
          data-testid="resume-continue-btn"
        >
          {isIT ? 'Continua la conversazione' : 'Continue conversation'}
          <ArrowRight size={22} />
        </button>
        
        {/* Alternative action */}
        <button
          onClick={handleGoHome}
          className="w-full mt-3 py-3 text-gray-500 text-sm hover:text-gray-700 transition-colors"
        >
          {isIT ? 'Inizia una nuova conversazione' : 'Start a new conversation'}
        </button>
      </div>
    </div>
  );
};

export default ResumeWelcome;
